'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import { useFetchRecreateData } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ToolFormParams } from '@/app/types';
import { cn } from '@/app/utils';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { TTSSettings } from './_components/TTSSettings';
import TTSTextEditor from './_components/TTSTextEditor';
import { getTtsTextLength, TTS_LIMITS } from './constant';

interface TextToTtsSettings extends Record<string, unknown> {
  voice_id?: string;
  speed?: number;
  pitch?: number;
  volume?: number;
}

export default function TextToSpeechForm() {
  const t = useTranslations();

  const { text2ttsFormParams, updateText2TtsFormParams } = useStore((state) => ({
    text2ttsFormParams: state.text2ttsFormParams,
    updateText2TtsFormParams: state.updateText2TtsFormParams,
  }));

  const settings = (text2ttsFormParams.settings || {}) as TextToTtsSettings;
  useFetchRecreateData();

  const updateSettings = useCallback(
    (newSettings: Record<string, unknown>) => {
      updateText2TtsFormParams('settings', {
        ...settings,
        ...newSettings,
      } as ToolFormParams['settings']);
    },
    [settings, updateText2TtsFormParams],
  );

  const handleTextChange = useCallback(
    (text: string) => {
      if (getTtsTextLength(text) > TTS_LIMITS.MAX_TEXT_LENGTH) {
        return;
      }
      updateText2TtsFormParams('textPrompt', text);
    },
    [updateText2TtsFormParams],
  );

  const editorText = text2ttsFormParams.textPrompt || '';
  const hasText = editorText.trim().length > 0;
  const textLength = useMemo(() => getTtsTextLength(editorText), [editorText]);
  const exceedsTextLimit = textLength > TTS_LIMITS.MAX_TEXT_LENGTH;

  const disabledTip = useMemo(() => {
    if (exceedsTextLimit) {
      return t('tools.textToSpeech.validation.textTooLong');
    }
    if (!hasText) {
      return t('tools.textToSpeech.validation.textRequired');
    }
    return '';
  }, [exceedsTextLimit, hasText, t]);

  return (
    <ToolFormContainer
      className="bg-[#0a0d10]"
      contentClassName="min-h-0 flex-1"
      submitButtonClassName="pt-0 pb-0"
      title={t('tools.textToSpeech.title')}
      mediaSection={
        <div className="min-h-[240px] flex-1 basis-[50vh] sm:flex-none [&>*]:h-full [&_.ProseMirror]:h-full [&_.ProseMirror]:max-h-none">
          <TTSTextEditor value={editorText} onChange={handleTextChange} />
        </div>
      }
      settingsSection={
        <TTSSettings
          voiceId={(settings.voice_id as string) || ''}
          speed={(settings.speed as number) ?? 1.0}
          pitch={(settings.pitch as number) ?? 0}
          volume={(settings.volume as number) ?? 0}
          onVoiceChange={(voiceId) => updateSettings({ voice_id: voiceId })}
          onSpeedChange={(value) => updateSettings({ speed: value })}
          onPitchChange={(value) => updateSettings({ pitch: value })}
          onVolumeChange={(value) => updateSettings({ volume: value })}
        />
      }
      submitButton={
        <div className="flex w-full items-center justify-center gap-4 sm:flex-col sm:items-stretch">
          <ToolFormSubmitButton
            className={cn(
              'h-12 w-full max-w-[300px] rounded-12 font-semibold text-[18px] hover:opacity-95 sm:max-w-none',
              disabledTip
                ? 'bg-system-neutral02 text-system-white24'
                : 'bg-[linear-gradient(90deg,#d3cfff_0%,#80d1ff_100%)] text-black',
            )}
            taskType="tts"
            textCreditsMeasuredLength={textLength}
            disabled={!!disabledTip}
            disabledTip={disabledTip}
          >
            {t('create.createForm.submit')}
          </ToolFormSubmitButton>
        </div>
      }
    />
  );
}
